import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/shared/utils/cn';
import { useToday } from '@/shared/hooks/useToday';
import { Select } from './Select';

export type DatePickerProps = {
  id: string;
  /** Valor en formato ISO `YYYY-MM-DD`. */
  value?: string;
  onValueChange?: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  /** Año mínimo seleccionable (incl.). Por defecto 1920. */
  fromYear?: number;
  /** Año máximo seleccionable (incl.). Por defecto el año actual. */
  toYear?: number;
  /** Primer día seleccionable, ISO `YYYY-MM-DD`. Afina `fromYear` al día. */
  minDate?: string;
  /** Último día seleccionable, ISO `YYYY-MM-DD`. Afina `toYear` al día. */
  maxDate?: string;
  /** Locale BCP-47 para nombres de mes/día. Por defecto `es`. */
  locale?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
};

const pad = (value: number) => String(value).padStart(2, '0');

const toISODate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseISODate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const clampMonth = (date: Date, min: Date, max: Date) => {
  if (date < min) return min;
  if (date > max) return max;
  return date;
};

export function DatePicker({
  id,
  value = '',
  onValueChange,
  onBlur,
  hasError,
  placeholder,
  disabled,
  className,
  name,
  fromYear: fromYearProp,
  toYear: toYearProp,
  minDate,
  maxDate,
  locale = 'es',
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}: DatePickerProps) {
  const generatedId = useId();
  const baseId = id || generatedId;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const today = useToday();

  const selected = useMemo(() => parseISODate(value), [value]);

  const minDay = useMemo(() => parseISODate(minDate), [minDate]);
  const maxDay = useMemo(() => parseISODate(maxDate), [maxDate]);
  const fromYear = fromYearProp ?? minDay?.getFullYear() ?? 1920;
  const toYear = toYearProp ?? maxDay?.getFullYear() ?? today?.getFullYear() ?? fromYear;

  const rangeStart = useMemo(
    () => (minDay ? startOfMonth(minDay) : new Date(fromYear, 0, 1)),
    [fromYear, minDay],
  );
  const rangeEnd = useMemo(
    () => (maxDay ? startOfMonth(maxDay) : new Date(toYear, 11, 1)),
    [maxDay, toYear],
  );
  const firstDay = minDay ?? rangeStart;
  const lastDay = maxDay ?? new Date(toYear, 11, 31);

  const [viewDate, setViewDate] = useState<Date>(() =>
    clampMonth(startOfMonth(selected ?? today ?? rangeEnd), rangeStart, rangeEnd),
  );

  // Reabrir/cambiar el valor reposiciona la vista en el mes seleccionado.
  useEffect(() => {
    if (isOpen) {
      setViewDate(clampMonth(startOfMonth(selected ?? today ?? new Date()), rangeStart, rangeEnd));
    }
  }, [isOpen, selected, rangeStart, rangeEnd, today]);

  const { monthNames, weekdayNames, formatLong } = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
    const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const longFormatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return {
      monthNames: Array.from({ length: 12 }, (_, month) =>
        monthFormatter.format(new Date(2020, month, 1)),
      ),
      // 2021-02-01 cae lunes: genera lu, ma, mi, ju, vi, sá, do.
      weekdayNames: Array.from({ length: 7 }, (_, index) =>
        weekdayFormatter.format(new Date(2021, 1, 1 + index)),
      ),
      formatLong: (date: Date) => longFormatter.format(date),
    };
  }, [locale]);

  const monthOptions = useMemo(
    () => monthNames.map((label, index) => ({ label, value: String(index) })),
    [monthNames],
  );

  const yearOptions = useMemo(() => {
    const years: { label: string; value: string }[] = [];
    for (let year = toYear; year >= fromYear; year -= 1) {
      years.push({ label: String(year), value: String(year) });
    }
    return years;
  }, [fromYear, toYear]);

  const gridDays = useMemo(() => {
    const first = startOfMonth(viewDate);
    const mondayOffset = (first.getDay() + 6) % 7;
    const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - mondayOffset);
    return Array.from(
      { length: 42 },
      (_, index) =>
        new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index),
    );
  }, [viewDate]);

  const close = () => {
    setIsOpen(false);
    onBlur?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    // Los selectores de mes y año pintan sus opciones en un portal fuera del
    // calendario: un clic ahí no es un clic afuera, aunque el árbol diga que sí.
    const handlePointerDown = (event: PointerEvent) => {
      const path = event.composedPath();
      if (path.includes(rootRef.current!)) return;
      if (path.some((node) => node instanceof HTMLElement && node.hasAttribute('data-adipa-select-panel'))) {
        return;
      }
      close();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
        onBlur?.();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onBlur]);

  const goToMonthOffset = (offset: number) => {
    setViewDate((current) =>
      clampMonth(
        new Date(current.getFullYear(), current.getMonth() + offset, 1),
        rangeStart,
        rangeEnd,
      ),
    );
  };

  const selectDay = (day: Date) => {
    onValueChange?.(toISODate(day));
    setIsOpen(false);
    onBlur?.();
    triggerRef.current?.focus();
  };

  const canGoPrev = startOfMonth(viewDate) > rangeStart;
  const canGoNext = startOfMonth(viewDate) < rangeEnd;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-adipa-md border bg-adipa-surface px-3 py-2',
          'text-left text-sm font-normal text-adipa-text-primary',
          'transition-colors duration-adipa-fast outline-none',
          isOpen
            ? 'border-adipa-primary ring-[3px] ring-adipa-focus-ring'
            : 'border-adipa-border hover:border-adipa-border-strong',
          hasError && 'border-adipa-error focus:border-adipa-error focus:ring-adipa-error-ring',
          disabled && 'cursor-not-allowed bg-adipa-surface-muted text-adipa-text-secondary opacity-70',
        )}
        onClick={() => {
          if (!disabled) setIsOpen((current) => !current);
        }}
        onBlur={() => {
          if (!isOpen) onBlur?.();
        }}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-adipa-text-placeholder')}>
          {selected ? formatLong(selected) : (placeholder ?? 'Selecciona una fecha')}
        </span>
        <svg
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-adipa-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div
          role="dialog"
          aria-label={ariaLabel ?? 'Selector de fecha'}
          className={cn(
            'absolute left-0 top-full z-30 mt-1 w-[19rem] max-w-[calc(100vw-2rem)]',
            'rounded-adipa-md border border-adipa-border bg-adipa-surface p-3 shadow-adipa-console-panel',
          )}
        >
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              aria-label="Mes anterior"
              disabled={!canGoPrev}
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-adipa-sm text-adipa-text-secondary',
                'transition-colors duration-adipa-fast outline-none',
                'hover:bg-adipa-surface-muted hover:text-adipa-primary',
                'focus-visible:ring-2 focus-visible:ring-adipa-focus-ring',
                'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-adipa-text-secondary',
              )}
              onClick={() => goToMonthOffset(-1)}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="min-w-0 flex-1">
                <Select
                  id={`${baseId}-month`}
                  aria-label="Mes"
                  options={monthOptions}
                  value={String(viewDate.getMonth())}
                  onValueChange={(next) =>
                    setViewDate((current) =>
                      clampMonth(
                        new Date(current.getFullYear(), Number(next), 1),
                        rangeStart,
                        rangeEnd,
                      ),
                    )
                  }
                />
              </div>
              <div className="min-w-0 flex-1">
                <Select
                  id={`${baseId}-year`}
                  aria-label="Año"
                  options={yearOptions}
                  value={String(viewDate.getFullYear())}
                  onValueChange={(next) =>
                    setViewDate((current) =>
                      clampMonth(
                        new Date(Number(next), current.getMonth(), 1),
                        rangeStart,
                        rangeEnd,
                      ),
                    )
                  }
                />
              </div>
            </div>

            <button
              type="button"
              aria-label="Mes siguiente"
              disabled={!canGoNext}
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-adipa-sm text-adipa-text-secondary',
                'transition-colors duration-adipa-fast outline-none',
                'hover:bg-adipa-surface-muted hover:text-adipa-primary',
                'focus-visible:ring-2 focus-visible:ring-adipa-focus-ring',
                'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-adipa-text-secondary',
              )}
              onClick={() => goToMonthOffset(1)}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-0.5">
            {weekdayNames.map((weekday, index) => (
              <div
                key={`${weekday}-${index}`}
                className="flex h-8 items-center justify-center text-xs font-normal text-adipa-text-secondary"
              >
                {weekday}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {gridDays.map((day) => {
              const inMonth = day.getMonth() === viewDate.getMonth();
              const inRange = day >= firstDay && day <= lastDay;
              const isSelected = selected ? isSameDay(day, selected) : false;
              const isToday = today ? isSameDay(day, today) : false;

              return (
                <button
                  key={toISODate(day)}
                  type="button"
                  disabled={!inRange}
                  aria-pressed={isSelected}
                  aria-label={formatLong(day)}
                  className={cn(
                    'flex h-9 w-full items-center justify-center rounded-adipa-sm text-sm',
                    'transition-colors duration-adipa-fast outline-none',
                    'focus-visible:ring-2 focus-visible:ring-adipa-focus-ring',
                    isSelected
                      ? 'bg-adipa-primary font-medium text-adipa-primary-foreground hover:bg-adipa-primary-hover'
                      : cn(
                          'hover:bg-adipa-surface-muted hover:text-adipa-primary',
                          inMonth ? 'text-adipa-text-primary' : 'text-adipa-text-placeholder',
                          isToday && 'font-semibold text-adipa-primary',
                        ),
                    !inRange && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                  )}
                  onClick={() => selectDay(day)}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
